import React, { useEffect, useRef, useState } from 'react';

/** Kupčeva ocena iz kućice za potpis. Komentar postoji samo uz jednu zvezdicu. */
export interface OcenaIzPotpisa {
  zvezdica: number;
  komentar?: string;
}

interface Props {
  onDone: (dataUrl: string, ocena: OcenaIzPotpisa | null) => void;
  /**
   * Da li se kupcu nude zvezdice. Samo kod kupca, i samo pri prvom potpisu
   * stanice — ocena se ne nudi nigde drugde, da je vozač ne bi davao sam.
   */
  ponudiOcenu?: boolean;
  onCancel: () => void;
  /** Zašto potpis nije sačuvan — mora da stoji U kućici, jer ona pokriva ekran. */
  message?: string | null;
}

/**
 * Potpis kupca prstom na ekranu.
 *
 * Interni dokaz o isporuci, bez poreske funkcije. Crta se na canvasu i pretvara
 * u sliku koja ide u red za slanje kao i svaki drugi zapis — potpis dat bez
 * signala mora da preživi do sledeće veze.
 */
export default function SignaturePad({ onDone, onCancel, message, ponudiOcenu = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [zvezdica, setZvezdica] = useState<number | null>(null);
  const [komentar, setKomentar] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Canvas mora dobiti stvarnu piksel-veličinu, inače je linija mutna i
    // pomerena u odnosu na prst na telefonima sa gustim ekranom.
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.scale(ratio, ratio);
    context.lineWidth = 2.5;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#1c2430';
  }, []);

  const positionOf = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    // Platno na ekranu ume da promeni veličinu posle merenja (polje za
    // komentar, poruka, okretanje telefona), a slika u njemu ostaje ista.
    // Preslikavanje se zato računa pri svakom dodiru — da linija prati prst
    // (17.10.2026). Dok je veličina ista, ovo je isto što i merenje na početku.
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      context.setTransform(canvas.width / rect.width, 0, 0, canvas.height / rect.height, 0, 0);
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    setHasInk(true);

    const { x, y } = positionOf(event);
    context.beginPath();
    context.moveTo(x, y);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;

    const context = canvasRef.current?.getContext('2d');
    if (!context) return;

    const { x, y } = positionOf(event);
    context.lineTo(x, y);
    context.stroke();
  };

  const stop = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    setZvezdica(null);
    setKomentar('');
  };

  return (
    <div className="signature">
      <p className="signature__title">Potpis kupca</p>
      {message && <p className="driver__message">{message}</p>}

      <canvas
        ref={canvasRef}
        className="signature__canvas"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerLeave={stop}
      />

      {/* OCENA. Vidi se i radi tek kad kupac povuče potpis — tada telefon drži
          on, ne vozač. Nije obavezna: „Sačuvaj potpis" radi i bez nje. Zahtev
          vlasnika, 16.10.2026.

          MESTO ZA NJU STOJI OD POČETKA, nevidljivo. Do 17.10.2026. se blok
          ubacivao tek posle prvog poteza: platno nije moglo da se skupi, pa su
          dugmad, i „Sačuvaj potpis", bežala ispod ivice ekrana. Pomagalo je tek
          okretanje telefona. */}
      {ponudiOcenu && (
        <div
          className="ocena"
          role="group"
          aria-label="Ocena dostave"
          aria-hidden={!hasInk}
          style={hasInk ? undefined : { visibility: 'hidden' }}
        >
          <p className="ocena__pitanje">
            Kako ste zadovoljni dostavom? <span className="muted">(nije obavezno)</span>
          </p>
          <div className="ocena__zvezdice">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={zvezdica !== null && n <= zvezdica ? 'ocena__zvezda ocena__zvezda--puna' : 'ocena__zvezda'}
                aria-label={`${n} od 5`}
                aria-pressed={zvezdica === n}
                disabled={!hasInk}
                tabIndex={hasInk ? undefined : -1}
                onClick={() => setZvezdica(zvezdica === n ? null : n)}
              >
                ★
              </button>
            ))}
          </div>
          {zvezdica === 1 && (
            <textarea
              className="ocena__komentar"
              rows={2}
              maxLength={500}
              placeholder="Šta nije bilo u redu? (nije obavezno)"
              value={komentar}
              onChange={(e) => setKomentar(e.target.value)}
            />
          )}
        </div>
      )}

      <div className="signature__actions">
        <button type="button" onClick={onCancel}>Odustani</button>
        <button type="button" onClick={clear} disabled={!hasInk}>Obriši</button>
        <button
          type="button"
          className="primary"
          disabled={!hasInk}
          onClick={() => {
            const url = canvasRef.current?.toDataURL('image/png');
            if (!url) return;
            const ocena = ponudiOcenu && zvezdica !== null
              ? { zvezdica, komentar: zvezdica === 1 && komentar.trim() ? komentar.trim() : undefined }
              : null;
            onDone(url, ocena);
          }}
        >
          Sačuvaj potpis
        </button>
      </div>
    </div>
  );
}
